import type {
  AdminMaintenanceStatus,
  AdminUserResource,
  AdminUserResourcePage,
  CleanupStatus,
  SystemMediaStorageStatus,
} from '../../shared/admin';
import { ApiRequestError } from '../auth/authApi';

type ErrorPayload = { error?: { code: string; message: string; fields?: Record<string, string> } };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/odevlendir/api/admin${path}`, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    ...init,
  });
  if (response.ok) return (await response.json()) as T;
  const payload = response.headers.get('content-type')?.includes('application/json')
    ? ((await response.json()) as ErrorPayload)
    : undefined;
  throw new ApiRequestError(response.status, payload?.error);
}

export async function listAdminUsers(offset = 0): Promise<AdminUserResourcePage> {
  const payload = await request<{ success: true; data: AdminUserResourcePage }>(
    `/users?limit=100&offset=${offset}`,
  );
  return payload.data;
}

export async function updateUserResourceLimits(
  userId: string,
  mediaLimitBytes: number | null,
  activePublicationLimit: number | null,
): Promise<AdminUserResource> {
  const payload = await request<{ success: true; data: { user: AdminUserResource } }>(
    `/users/${encodeURIComponent(userId)}/limits`,
    { method: 'PATCH', body: JSON.stringify({ mediaLimitBytes, activePublicationLimit }) },
  );
  return payload.data.user;
}

export async function getAdminMaintenance(): Promise<AdminMaintenanceStatus> {
  const payload = await request<{ success: true; data: AdminMaintenanceStatus }>('/maintenance');
  return payload.data;
}

export async function runAdminCleanup(): Promise<CleanupStatus> {
  const payload = await request<{ success: true; data: { cleanup: CleanupStatus } }>(
    '/maintenance/cleanup',
    { method: 'POST', body: JSON.stringify({}) },
  );
  return payload.data.cleanup;
}

export async function getSystemMediaStorage(): Promise<SystemMediaStorageStatus> {
  const payload = await request<{ success: true; data: { storage: SystemMediaStorageStatus } }>(
    '/storage',
  );
  return payload.data.storage;
}

export async function updateSystemMediaLimit(
  limitBytes: number,
  confirmBelowCurrentUsage: boolean,
): Promise<SystemMediaStorageStatus> {
  const payload = await request<{ success: true; data: { storage: SystemMediaStorageStatus } }>(
    '/storage/limit',
    {
      method: 'PATCH',
      body: JSON.stringify({ limitBytes, confirmBelowCurrentUsage }),
    },
  );
  return payload.data.storage;
}
